import React, { useState, useEffect } from 'react';
import { Award } from '../../../types/award';
import { awardService } from '../../../services/awardService';
import { Link } from 'react-router-dom';
import { Button } from '../../ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import { Edit, Trash2, Eye } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useToast } from '../../ui/use-toast';

export const AwardTable = () => {
  const [awards, setAwards] = useState<Award[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  const fetchAwards = async () => {
    try {
      const data = await awardService.getAllAwards();
      setAwards(data);
    } catch (error) {
      console.error('Erreur lors de la récupération des awards:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger la liste des awards',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet award ?')) {
      return;
    }

    try {
      await awardService.deleteAward(id);
      toast({
        title: 'Succès',
        description: 'L\'award a été supprimé avec succès',
      });
      fetchAwards();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de supprimer l\'award',
        variant: 'destructive',
      });
    }
  };

  useEffect(() => {
    fetchAwards();
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'text-green-400';
      case 'upcoming':
        return 'text-blue-400';
      case 'ended':
        return 'text-gray-400';
      default:
        return 'text-gray-400';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return 'Actif';
      case 'upcoming':
        return 'À venir';
      case 'ended':
        return 'Terminé';
      default:
        return status;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Table>
        <TableHeader>
          <TableRow className="border-white/10">
            <TableHead className="text-gray-200">Nom</TableHead>
            <TableHead className="text-gray-200">Description</TableHead>
            <TableHead className="text-gray-200">Catégories</TableHead>
            <TableHead className="text-gray-200">Date de début</TableHead>
            <TableHead className="text-gray-200">Date de fin</TableHead>
            <TableHead className="text-gray-200">Statut</TableHead>
            <TableHead className="text-right text-gray-200">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {awards.map((award) => (
            <TableRow key={award.id} className="border-white/10">
              <TableCell className="font-medium text-white">{award.name}</TableCell>
              <TableCell className="text-gray-300">{award.description}</TableCell>
              <TableCell className="text-gray-300">{award.award_categories?.length || 0} catégories</TableCell>
              <TableCell className="text-gray-300">
                {format(new Date(award.start_date), 'dd MMM yyyy', { locale: fr })}
              </TableCell>
              <TableCell className="text-gray-300">
                {format(new Date(award.end_date), 'dd MMM yyyy', { locale: fr })}
              </TableCell>
              <TableCell>
                <span className={getStatusColor(award.status)}>
                  {getStatusText(award.status)}
                </span>
              </TableCell>
              <TableCell className="text-right space-x-2">
                <Button
                  variant="ghost"
                  size="icon"
                  asChild
                  className="hover:bg-white/10"
                >
                  <Link to={`/awards/${award.slug}`} target="_blank">
                    <Eye className="h-4 w-4 text-gray-300 hover:text-white" />
                  </Link>
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  asChild
                  className="hover:bg-white/10"
                >
                  <Link to={`/admin/awards/edit/${award.slug}`}>
                    <Edit className="h-4 w-4 text-gray-300 hover:text-white" />
                  </Link>
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(award.id)}
                  className="hover:bg-red-500/10"
                >
                  <Trash2 className="h-4 w-4 text-red-300 hover:text-red-500" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
          {awards.length === 0 && (
            <TableRow className="border-white/10">
              <TableCell colSpan={7} className="text-center py-8 text-gray-400">
                Aucun award trouvé
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};
